import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsentBanner from './components/CookieConsentBanner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  // Wait for stored login state to hydrate before deciding whether to redirect.
  if (loading) {
    return <div className="route-loading" role="status">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Allow access if user is admin or if no specific roles required, or if user's role is in allowed roles
  const hasAccess = user.is_admin || allowedRoles.length === 0 || allowedRoles.includes(user.account_type);

  if (!hasAccess) {
    return <div>Access denied. You don't have permission to view this page.</div>;
  }

  return children;
};

// Public Route Component - Restricts admin/creator access to public pages
const PublicRoute = ({ children, allowCreators = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="route-loading" role="status">Loading...</div>;
  }

  // If a special route is allowed for creators/admins, skip the redirect.
  if (allowCreators) {
    return children;
  }

  // If user is admin or creator, redirect them to their dashboard (except for allowed public pages)
  if (user && (user.account_type === 'admin' || user.account_type === 'creator' || user.is_admin)) {
    const dashboardPath = user.account_type === 'creator' ? '/creator-dashboard' : '/admin';
    return <Navigate to={dashboardPath} replace />;
  }

  return children;
};

// Lazy load components
const AuthPage = lazy(() => import('./components/AuthPage'));
const AccountDeleted = lazy(() => import('./components/AccountDeleted'));
  const LandingPage = lazy(() => import('./components/LandingPage'));
  const Home = lazy(() => import('./components/Home'));
const EventDetails = lazy(() => import('./components/EventDetails'));
const MyBookings = lazy(() => import('./components/MyBookings'));
const CreateEvent = lazy(() => import('./components/CreateEvent'));
const ManageEvents = lazy(() => import('./components/ManageEvents'));
const Profile = lazy(() => import('./components/Profile'));
const MfaSettings = lazy(() => import('./components/MfaSettings'));
const Payment = lazy(() => import('./components/Payment'));
const PaymentMethodSelection = lazy(() => import('./components/PaymentMethodSelection'));
const StripePayment = lazy(() => import('./components/StripePayment'));
const BookingConfirmation = lazy(() => import('./components/BookingConfirmation'));
const BankAccounts = lazy(() => import('./components/BankAccounts'));
const TicketValidator = lazy(() => import('./components/TicketValidator'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ConfirmEmail = lazy(() => import('./components/ConfirmEmail'));
const MultiStageRegistration = lazy(() => import('./components/MultiStageRegistration'));
const About = lazy(() => import('./components/About'));
const Support = lazy(() => import('./components/Support'));
const BrowseEvents = lazy(() => import('./components/BrowseEvents'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const EventCheckIn = lazy(() => import('./components/EventCheckIn'));
const MySpending = lazy(() => import('./components/MySpending'));
  const CreatorDashboard = lazy(() => import('./components/CreatorDashboard'));
  const Revenue = lazy(() => import('./components/Revenue'));
  const TicketValidation = lazy(() => import('./components/TicketValidation'));
  const CheckIn = lazy(() => import('./components/CheckIn'));
  const DiscountCodes = lazy(() => import('./components/DiscountCodes'));
  const Policies = lazy(() => import('./components/Policies'));
  const NotFound = lazy(() => import('./components/NotFound'));
  const Forbidden = lazy(() => import('./components/Forbidden'));
  const ServiceUnavailable = lazy(() => import('./components/ServiceUnavailable'));
  const ManageAdmins = lazy(() => import('./components/ManageAdmins'));
  const ScanTickets = lazy(() => import('./components/ScanTickets'));
  const EditEvent = lazy(() => import('./components/EditEvent'));
  const AdvertBuilder = lazy(() => import('./components/AdvertBuilder'));
const LiveAdverts = lazy(() => import('./components/LiveAdverts'));
  const AdvertView = lazy(() => import('./components/AdvertView'));


// Notification setup component
const NotificationSetup = () => {
  const { user } = useAuth();

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered successfully:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }

    // Request notification permission
    const requestNotificationPermission = async () => {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
          console.log('Notification permission granted');
          // You could send the subscription to your server here for push notifications
        } else if (permission === 'denied') {
          console.log('Notification permission denied');
        }
      }
    };

    // Only request permission for logged-in users
    if (user && Notification.permission === 'default') {
      // Small delay to avoid being too intrusive
      setTimeout(() => {
        requestNotificationPermission();
      }, 3000);
    }
  }, [user]);

  return null; // This component doesn't render anything
};

function MainContent() {
  const { user } = useAuth();
  const location = useLocation();

  const fullWidthPaths = ['/', '/browse', '/about', '/support', '/policies', '/privacy-policy', '/terms', '/cookie-policy', '/acceptable-use-policy', '/mfa-setup'];
  const isFullWidthPage = fullWidthPaths.includes(location.pathname) || location.pathname.startsWith('/advert/');
  const hasSidebar = !isFullWidthPage && user && (user.account_type === 'creator' || user.account_type === 'admin' || user.is_admin);

  return (
      <div className={`main-content ${hasSidebar ? 'with-sidebar' : ''}`}>
        <NotificationSetup />
        <Suspense fallback={<div className="route-loading">Loading...</div>}>
          <Routes>
              <Route path="/" element={<PublicRoute allowCreators={true}><LandingPage /></PublicRoute>} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/join" element={<AuthPage />} />
              <Route path="/account-deleted" element={<AccountDeleted />} />
              <Route path="/register-creator" element={<MultiStageRegistration />} />
              <Route path="/confirm-email" element={<ConfirmEmail />} />
              <Route path="/event/:id" element={<PublicRoute><EventDetails /></PublicRoute>} />
              <Route path="/my-bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
              <Route path="/create-event" element={<ProtectedRoute allowedRoles={['creator']}><CreateEvent /></ProtectedRoute>} />
              <Route path="/manage-events" element={<ProtectedRoute allowedRoles={['creator']}><ManageEvents /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/mfa-setup" element={<ProtectedRoute><MfaSettings onboarding /></ProtectedRoute>} />
              <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
              <Route path="/payment-method/:eventId" element={<PaymentMethodSelection />} />
              <Route path="/payment/stripe/:bookingId" element={<StripePayment />} />
              <Route path="/booking-confirmation/:bookingId" element={<BookingConfirmation />} />
              <Route path="/bank-accounts" element={<ProtectedRoute><BankAccounts /></ProtectedRoute>} />
              <Route path="/ticket-validator" element={<ProtectedRoute allowedRoles={['creator']}><TicketValidator /></ProtectedRoute>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/about" element={<PublicRoute allowCreators={true}><About /></PublicRoute>} />
              <Route path="/support" element={<PublicRoute allowCreators={true}><Support /></PublicRoute>} />
              <Route path="/browse" element={<PublicRoute><BrowseEvents /></PublicRoute>} />
             <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
             <Route path="/event/:eventId/checkin" element={<ProtectedRoute allowedRoles={['creator']}><EventCheckIn /></ProtectedRoute>} />
             <Route path="/my-spending" element={<ProtectedRoute><MySpending /></ProtectedRoute>} />
             <Route path="/creator-dashboard" element={<ProtectedRoute allowedRoles={['creator', 'admin']}><CreatorDashboard /></ProtectedRoute>} />
              <Route path="/revenue" element={<ProtectedRoute allowedRoles={['creator', 'admin']}><Revenue /></ProtectedRoute>} />
              <Route path="/ticket-validation" element={<ProtectedRoute allowedRoles={['creator', 'admin']}><TicketValidation /></ProtectedRoute>} />
              <Route path="/check-in" element={<ProtectedRoute allowedRoles={['creator', 'admin']}><CheckIn /></ProtectedRoute>} />
              <Route path="/events/:eventId/discount-codes" element={<ProtectedRoute allowedRoles={['creator', 'admin']}><DiscountCodes /></ProtectedRoute>} />
              <Route path="/policies" element={<Policies />} />
             <Route path="/privacy-policy" element={<Policies />} />
             <Route path="/terms" element={<Policies />} />
             <Route path="/cookie-policy" element={<Policies />} />
             <Route path="/acceptable-use-policy" element={<Policies />} />
              <Route path="/event/:eventId/manage-admins" element={<ProtectedRoute allowedRoles={['creator']}><ManageAdmins /></ProtectedRoute>} />
              <Route path="/event/:eventId/scan-tickets" element={<ProtectedRoute allowedRoles={['creator']}><ScanTickets /></ProtectedRoute>} />
              <Route path="/events/:eventId/edit" element={<ProtectedRoute allowedRoles={['creator', 'admin']}><EditEvent /></ProtectedRoute>} />
               <Route path="/advert-builder" element={<ProtectedRoute allowedRoles={['creator']}><AdvertBuilder /></ProtectedRoute>} />
              <Route path="/live-adverts" element={<ProtectedRoute allowedRoles={['creator', 'admin']}><LiveAdverts /></ProtectedRoute>} />
              <Route path="/advert/:id" element={<PublicRoute allowCreators={true}><AdvertView /></PublicRoute>} />
             <Route path="/403" element={<Forbidden />} />
             <Route path="/503" element={<ServiceUnavailable />} />
             <Route path="*" element={<NotFound />} />
          </Routes>
         </Suspense>
         <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <Router>
          <div className="App">
            <CookieConsentBanner />
            <Header />
            <Sidebar />
            <MainContent />
          </div>
        </Router>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
